<template>
    <StepShell embedded layout="page">
        <div class="flex flex-col gap-3">
            <StatusChip v-if="published" state="done" class="self-start">{{ t("onboarding.profileSetup.networkStep.publishedChip") }}</StatusChip>
            <StepHeader :title="t('onboarding.profileSetup.networkStep.title')" :lede="t('onboarding.profileSetup.networkStep.lede')" />
        </div>

        <OrganizationProfileDirectoryContent
            embedded
            has-collaboration-profile
            :create-profile-url="inviteUrl"
            :invite-url="inviteUrl"
            source="guided_setup"
            @busy="isAdding = $event" />

        <div class="network-invitation">
            <div class="min-w-0">
                <h2 class="body-semibold text-ink">{{ t("onboarding.profileSetup.networkStep.inviteTitle") }}</h2>
                <p class="body-small text-ink-muted">{{ t("onboarding.profileSetup.networkStep.inviteBody") }}</p>
            </div>
            <PublicProfileRequestLinkAction
                :copied="isCopied"
                :link-copied-text="t('onboarding.profileSetup.networkStep.linkCopied')"
                :disabled="isCopying"
                hide-dismiss
                @copy="copyRequestLink" />
        </div>
        <BaseNotification v-if="copyFailed" theme="error" :text="t('onboarding.profileSetup.networkStep.copyFailed')" @dismiss="copyFailed = false" />
        <BaseToast v-model:open="isCopiedToastOpen" :description="t('onboarding.profileSetup.networkStep.linkCopiedToast')" />

        <StepActions flush>
            <template #start>
                <I18nT keypath="onboarding.profileSetup.networkStep.later" scope="global" tag="p" class="body-small text-ink-muted">
                    <template #link>
                        <BaseLink href="/people/" class="base-link">{{ t("onboarding.profileSetup.networkStep.yourPeople") }}</BaseLink>
                    </template>
                </I18nT>
            </template>
            <BaseButton theme="primary" :disabled="busy || isAdding" @click="emit('continue')">{{
                calendarLast ? t("onboarding.profileSetup.calendarLast.profile.continue") : t("onboarding.profileSetup.networkStep.done")
            }}</BaseButton>
        </StepActions>
    </StepShell>
</template>

<script setup lang="ts">
import StepActions from "~vue/CalendarFirstOnboarding/StepActions.vue";
import StepHeader from "~vue/CalendarFirstOnboarding/StepHeader.vue";
import StepShell from "~vue/CalendarFirstOnboarding/StepShell.vue";
import { copyShareUrl, profileInviteMessage } from "~vue/CollaborationProfile/composables/useProfileShareActions";
import { logProfileShared, withShareAttribution } from "~vue/CollaborationProfile/profileShareAttribution";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseToast from "~vue/components/BaseToast.vue";
import StatusChip from "~vue/components/StatusChip.vue";
import { useTimedShow } from "~vue/composables/useTimedShow";
import OrganizationProfileDirectoryContent from "~vue/Connections/OrganizationProfileDirectoryContent.vue";
import PublicProfileRequestLinkAction from "~vue/Connections/PublicProfileRequestLinkAction.vue";
import BaseLink from "~vue/router/BaseLink.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { ref } from "vue";
import { I18nT, useI18n } from "vue-i18n";

const { t } = useI18n();

const {
    inviteUrl,
    busy = false,
    published = true,
    calendarLast = false,
} = defineProps<{
    inviteUrl: string;
    busy?: boolean;
    /** False when the user kept the profile private, which calendar-last still routes through here. */
    published?: boolean;
    calendarLast?: boolean;
}>();
const emit = defineEmits<{ continue: [] }>();
const isAdding = ref(false);
const { show: showCopied, isShowing: isCopied } = useTimedShow({ duration: 10_000 });
const isCopying = ref(false);
const copyFailed = ref(false);
const isCopiedToastOpen = ref(false);

async function copyRequestLink(): Promise<void> {
    isCopying.value = true;
    copyFailed.value = false;
    if (await copyShareUrl(withShareAttribution(inviteUrl, "guided_setup", "link"), profileInviteMessage())) {
        showCopied();
        isCopiedToastOpen.value = true;
        void logUserInteraction(AmplitudeEventNames.CONNECTION_INVITE_COPIED, { source: "guided_setup" });
        void logProfileShared({ shareSource: "guided_setup", target: "link", shareKind: "invite" });
    } else {
        copyFailed.value = true;
    }
    isCopying.value = false;
}
</script>

<style scoped>
@reference "../../../css/tailwind.css";
@layer components {
    .network-invitation {
        @apply flex flex-col items-start gap-3 rounded-xl bg-surface-sunken p-5 md:flex-row md:items-center md:justify-between md:p-6;
    }
}
</style>
